import type { createClient } from 'contentful'
import type { StandardDocument } from '../types/cms'

export const useContentfulData = async <T extends StandardDocument>(
  slug: string,
  contentType: string,
  isPreview: boolean,
  t: Function,
  articleVariant: 'article' | 'article2' = 'article',
) => {
  const { $contentfulClient, $previewClient } = useNuxtApp()

  const contentfulClient = $contentfulClient as ReturnType<typeof createClient>
  const previewClient = $previewClient as ReturnType<typeof createClient>
  const route = useRoute()
  const asyncDataKey = `${articleVariant}-${contentType}-${slug}-${isPreview ? 'preview' : 'published'}`
  const fetchFields = async (client: ReturnType<typeof createClient>) => {
    const response = await client.getEntries({
      content_type: contentType,
      'fields.slug[in]': slug,
      limit: 1,
    })

    return response?.items?.[0]?.fields as unknown as T | undefined
  }

  const toNotFoundError = () => {
    const splitPath = route.path.split('/')
    const message = t('error.404.content', { slug })
    return createError({
      status: 404,
      message,
      data:
        splitPath.length >= 2
          ? {
              buttonText: t('error.404.standard'),
              buttonUrl: `/${splitPath[splitPath.length - 2]}`,
            }
          : undefined,
    })
  }

  if (isPreview) {
    const { data: contentfulPreviewData } = await useAsyncData<T | undefined>(
      asyncDataKey,
      async () => await fetchFields(previewClient),
      { deep: false },
    )
    if (contentfulPreviewData?.value) {
      return contentfulPreviewData.value as T
    }

    throw toNotFoundError()
  } else {
    const { data: contentfulData } = await useAsyncData<T | undefined>(
      asyncDataKey,
      async () => await fetchFields(contentfulClient),
      { deep: false },
    )

    if (contentfulData?.value) {
      return contentfulData.value as T
    }

    throw toNotFoundError()
  }
}
